/**
 * Device-side local image cache.
 * Saves generated and reference images to the user's local Cache Storage / IndexedDB,
 * ensuring they are never downloaded repeatedly from Firebase Storage.
 */

const CACHE_NAME = 'voxnela-studio-images-v1'
const memoryObjectUrlMap = new Map<string, string>()

/**
 * Checks if the browser supports Cache API
 */
function isCacheSupported(): boolean {
  return typeof window !== 'undefined' && 'caches' in window
}

/**
 * Stores an image blob directly in device cache under the given URL
 */
export async function cacheImageBlob(url: string, blob: Blob): Promise<void> {
  if (!isCacheSupported() || !url || url.startsWith('blob:') || url.startsWith('data:')) return

  try {
    const cache = await caches.open(CACHE_NAME)
    const response = new Response(blob, {
      headers: {
        'Content-Type': blob.type || 'image/png',
        'Cache-Control': 'public, max-age=31536000, immutable',
      },
    })
    await cache.put(url, response)
  } catch (err) {
    console.warn('Could not write image to Cache Storage:', err)
  }
}

/**
 * Pre-caches an image from its remote URL into local device storage.
 * If already cached, does nothing.
 */
export async function preloadAndCacheImage(url: string): Promise<string> {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) return url
  if (!isCacheSupported()) return url

  // Check in-memory map first
  if (memoryObjectUrlMap.has(url)) {
    return memoryObjectUrlMap.get(url)!
  }

  try {
    const cache = await caches.open(CACHE_NAME)
    const matched = await cache.match(url)
    if (matched) {
      const blob = await matched.blob()
      const objectUrl = URL.createObjectURL(blob)
      memoryObjectUrlMap.set(url, objectUrl)
      return objectUrl
    }

    // Fetch once and cache forever
    const response = await fetch(url, { mode: 'cors' })
    if (response.ok) {
      const cloned = response.clone()
      await cache.put(url, cloned)
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      memoryObjectUrlMap.set(url, objectUrl)
      return objectUrl
    }
  } catch (err) {
    console.warn('Failed to pre-cache image:', err)
  }

  return url
}

/**
 * Returns a fast local blob URL if cached on device, otherwise returns the original URL.
 */
export async function getLocalCachedImageUrl(url: string): Promise<string> {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) return url
  if (!isCacheSupported()) return url

  if (memoryObjectUrlMap.has(url)) {
    return memoryObjectUrlMap.get(url)!
  }

  try {
    const cache = await caches.open(CACHE_NAME)
    const matched = await cache.match(url)
    if (matched) {
      const blob = await matched.blob()
      const objectUrl = URL.createObjectURL(blob)
      memoryObjectUrlMap.set(url, objectUrl)
      return objectUrl
    }
  } catch {
    // ignore
  }

  return url
}
